import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AccountFragment,
  AccountSettingsInput,
  AddressInput,
} from 'src/graphql/generated/graphql'
import { useAccount, useActions } from 'src/hooks'
import { queryKeyStore } from '../queryKeyStore'
import {
  addFreeCampaign,
  changeSubscription,
  createAccount,
  createAffiliate,
  createFreeAffiliate,
  createOrUpdateBackPanel,
  createSparseUser,
  loginUser,
  purchaseProduct,
  revertBulkOrder,
  sendMagicLink,
  updateAccount,
  updateCard,
  updateUserLabelOverrides,
  upgradeToAffiliate,
  upgradeToGreenerStillAffiliate,
  upgradeToReferringCustomer,
} from './fetching'
import * as fetching from './fetching'

export const useAcceptCampaignShare = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: fetching.acceptCampaignShare,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.alerts.infinite._def,
      })
    },
  })
  return mutation
}

export const useAcceptMembershipInvite = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: fetching.acceptMembershipInvite,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.alerts.infinite._def,
      })
    },
  })
  return mutation
}

export const useAcceptSharedContact = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: fetching.acceptSharedContact,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.alerts.infinite._def,
      })
    },
  })
  return mutation
}

export const useBulkDeleteCards = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.bulkDeleteCards,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.customCard._def })
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.draftedCard._def,
      })
    },
  })
}

export const useBulkDeleteSendableCards = () => {
  return useMutation({ mutationFn: fetching.bulkDeleteSendableCards })
}

export const useDeclineMembershipInvite = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: fetching.declineMembershipInvite,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.alerts.infinite._def,
      })
    },
  })
  return mutation
}

export const useDeleteCampaignShare = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: fetching.deleteCampaignShare,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.alerts.infinite._def,
      })
    },
  })
  return mutation
}

export const useDeleteContactShare = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: fetching.deleteContactShare,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.alerts.infinite._def,
      })
    },
  })
  return mutation
}

export const useDeleteReminder = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: fetching.deleteReminder,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.alerts.infinite._def,
      })
    },
  })
  return mutation
}

export const useDenyNotification = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: fetching.denyNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.alerts.infinite._def,
      })
    },
  })
  return mutation
}

export const useCancelStripeSubscription = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: fetching.cancelStripeSubscription,
    onSuccess: data => {
      if (data.account) {
        queryClient.setQueryData<AccountFragment>(
          queryKeyStore.account.detail.queryKey,
          data.account,
        )
      }
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.subscription._def,
      })
    },
  })
  return mutation
}

export const useCreateStripeOrder = () => {
  const mutation = useMutation({
    mutationFn: fetching.createStripeOrder,
  })
  return mutation
}

export const useCreateAccount = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: createAccount,
    onSuccess: data => {
      if (data.createAccount.account) {
        queryClient.setQueryData<AccountFragment>(
          queryKeyStore.account.detail.queryKey,
          data.createAccount.account,
        )
      }
    },
  })

  return mutation
}

export const useCreateAffiliate = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: createAffiliate,
    onSuccess: data => {
      if (data.createDistributor.account) {
        queryClient.setQueryData<AccountFragment>(
          queryKeyStore.account.detail.queryKey,
          data.createDistributor.account,
        )
      }
    },
  })

  return mutation
}

export const useCreateFreeAffiliate = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: createFreeAffiliate,
    onSuccess: data => {
      if (data.createFreeAffiliate.account) {
        queryClient.setQueryData<AccountFragment>(
          queryKeyStore.account.detail.queryKey,
          data.createFreeAffiliate.account,
        )
      }
    },
  })

  return mutation
}

export const useCreateUserLoginLink = () => {
  return useMutation({ mutationFn: fetching.createUserLoginLink })
}

export const useRequestPhoneNumberVerification = () => {
  return useMutation({ mutationFn: fetching.requestPhoneNumberVerification })
}

export const useValidatePhoneNumber = () => {
  return useMutation({
    mutationFn: fetching.validatePhoneNumber,
  })
}

export const usePhoneNumberVerification = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: fetching.verifyPhoneNumber,
    onSuccess: data => {
      if (
        data.verifyPhoneNumber.__typename === 'VerifyPhoneNumberSuccess' &&
        data.verifyPhoneNumber &&
        data.verifyPhoneNumber.verifiedAccount
      ) {
        queryClient.setQueryData<AccountFragment>(
          queryKeyStore.account.detail.queryKey,
          data.verifyPhoneNumber.verifiedAccount,
        )
      }
    },
  })
  return mutation
}

export const usePhoneNumberVerifyForResource = () => {
  return useMutation({
    mutationFn: fetching.phoneNumberVerifyForResource,
  })
}

export const useValidateEmail = () => {
  return useMutation({
    mutationFn: fetching.validateEmail,
  })
}

export const useRequestEmailVerificationCode = () => {
  return useMutation({
    mutationFn: fetching.requestEmailVerificationCode,
  })
}

export const useValidateEmailVerificationCode = () => {
  return useMutation({
    mutationFn: fetching.validateEmailVerificationCode,
  })
}

export const useValidateUsername = () => {
  return useMutation({
    mutationFn: fetching.validateUsername,
  })
}

export const useMagicLink = () => {
  return useMutation({ mutationFn: sendMagicLink })
}

export const useLoginUser = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: loginUser,
    onSuccess: data => {
      if (data.loginUser.account) {
        queryClient.setQueryData<AccountFragment>(
          queryKeyStore.account.detail.queryKey,
          data.loginUser.account,
        )
      }
    },
  })
}

export const useCreateCard = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: fetching.createCard,
    onSuccess: data => {
      if (data.createCard.card) {
        queryClient.setQueryData(
          queryKeyStore.card.detail({ id: data.createCard.card.id }).queryKey,
          { card: data.createCard.card },
        )
      }
      if (data.createCard.account) {
        queryClient.setQueryData<AccountFragment>(
          queryKeyStore.account.detail.queryKey,
          data.createCard.account,
        )
      }
    },
  })
}

export const useCancelPendingCards = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: fetching.cancelPendingCards,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.sent_card._def })
    },
  })
}

export const useDigitalCardCreate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: fetching.digitalCardCreate,
    onSuccess: data => {
      if (data?.card) {
        queryClient.invalidateQueries(
          queryKeyStore.card.detail({
            id: data.card.id,
          }),
        )
      }
    },
  })
}

export const useDigitalCardOrderCreate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.digitalCardOrderCreate,
    onSuccess: (_data, variables) => {
      // TODO: could just set the query data here if we had the correct return type
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.digital_card_order.detail({
          id: variables.digitalCardPk,
        }).queryKey,
      })
    },
  })
}

export const useDigitalCardOrderPay = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.digitalCardOrderPay,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.card._def })
      queryClient.invalidateQueries(
        queryKeyStore.digital_card_order.detail({
          id: variables.digitalCardPk,
        }),
      )
      setTimeout(() => {
        queryClient.resetQueries({ queryKey: queryKeyStore.upsell._def })
      }, 5000)
    },
  })
}

export const useDigitalCardRecipientFromNameUpsert = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.digitalCardRecipientFromNameUpsert,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.digital_card_order.detail({
          id: variables.digitalCardPk,
        }).queryKey,
      })
    },
  })
}

export const useDigitalCardUpdate = () => {
  return useMutation({
    mutationFn: fetching.digitalCardUpdate,
  })
}

export const useDigitalCardOrderLineGiftCardAdd = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: fetching.digitalCardOrderLineGiftCardAdd,
    onSuccess: data => {
      if (data) {
        queryClient.invalidateQueries({
          queryKey: queryKeyStore.card._def,
        })
        queryClient.invalidateQueries({
          queryKey: queryKeyStore.digitalCard._def,
        })
        queryClient.invalidateQueries({
          queryKey: queryKeyStore.digital_card_order._def,
        })
      }
    },
  })
}

export const useDigitalCardOrderLineGiftCardRemove = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: fetching.digitalCardOrderLineGiftCardRemove,
    onSuccess: data => {
      if (data) {
        queryClient.invalidateQueries({
          queryKey: queryKeyStore.card._def,
        })
        queryClient.invalidateQueries({
          queryKey: queryKeyStore.digitalCard._def,
        })
        queryClient.invalidateQueries({
          queryKey: queryKeyStore.digital_card_order._def,
        })
      }
    },
  })
}

export const useUpdateDigitalCardStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: fetching.digitalCardUpdateShareStatus,
    onSuccess: data => {
      if (data.digitalCardUpdateShareStatus.digitalCard) {
        queryClient.invalidateQueries({
          queryKey: queryKeyStore.digitalCard._def,
        })
      }
    },
  })
}

export const useDigitalCardRecipientCreate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.digitalCardRecipientCreate,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(
        queryKeyStore.digital_card_order.detail({
          id: variables.digitalCardPk,
        }),
      )
    },
  })
}

export const useDigitalCardRecipientLinkToAccount = () => {
  return useMutation({
    mutationFn: fetching.digitalCardRecipientLinkToAccount,
  })
}

export const useDigitalGiftCardsRedeem = () => {
  return useMutation({
    mutationFn: fetching.digitalGiftCardsRedeem,
  })
}

export const useDigitalCardAddPhoneNumberToAccount = () => {
  const queryClient = useQueryClient()
  const actions = useActions()
  return useMutation({
    mutationFn: fetching.digitalCardAddPhoneNumberToAccount,
    onSuccess: data => {
      if (
        data.result.__typename ===
          'DigitalCardAddPhoneNumberToAccountSuccess' &&
        data.result.account
      ) {
        queryClient.setQueryData<AccountFragment>(
          queryKeyStore.account.detail.queryKey,
          data.result.account,
        )
        actions.updatedAccount(data.result.account)
      }
    },
  })
}

export const useDeleteDigitalCard = () => {
  return useMutation({
    mutationFn: fetching.digitalCardDelete,
  })
}

export const useRemoveUserUpsell = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.removeUserUpsell,
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: queryKeyStore.upsell._def })
    },
  })
}

export const useSubscriptionAddonAdd = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.subscriptionAddonAdd,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeyStore.account.detail)
      queryClient.resetQueries(queryKeyStore.subscription.list)
    },
  })
}

export const useSubscriptionAddonRemove = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.subscriptionAddonRemove,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeyStore.account.detail)
      queryClient.resetQueries(queryKeyStore.subscription.list)
    },
  })
}

export const useFavoriteSendableCard = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.favoriteSendableCard,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(
        queryKeyStore.sendableCard.detail({ id: variables.id }),
      )
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.sendableCard.infinite._def,
      })
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.cardCatalog._def,
      })
    },
  })
}

export const useRequestPhysicalCard = () => {
  return useMutation({
    mutationFn: fetching.requestPhysicalCard,
  })
}

export const useCreateSparseUser = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createSparseUser,
    onSuccess: data => {
      if (data.createSparseUser.account) {
        queryClient.setQueryData<AccountFragment>(
          queryKeyStore.account.detail.queryKey,
          data.createSparseUser.account,
        )
      }
    },
  })
}

export const usePurchaseProduct = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: purchaseProduct,
    onSuccess: data => {
      if (data.purchaseProduct.account) {
        queryClient.setQueryData<AccountFragment>(
          queryKeyStore.account.detail.queryKey,
          data.purchaseProduct.account,
        )
      }
    },
  })
}

export const useUnfavoriteSendableCard = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.unfavoriteSendableCard,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(
        queryKeyStore.sendableCard.detail({ id: variables.id }),
      )
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.sendableCard.infinite._def,
      })
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.cardCatalog._def,
      })
    },
  })
}

export const useUpgradeToAffiliate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: upgradeToAffiliate,
    onSuccess: data => {
      if (data.upgradeToAffiliate.account) {
        queryClient.setQueryData<AccountFragment>(
          queryKeyStore.account.detail.queryKey,
          data.upgradeToAffiliate.account,
        )
      }
    },
  })
}

export const useUpgradeToGreenerStillAffiliate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: upgradeToGreenerStillAffiliate,
    onSuccess: data => {
      if (data.upgradeToGreenerStillAffiliate.account) {
        queryClient.setQueryData<AccountFragment>(
          queryKeyStore.account.detail.queryKey,
          data.upgradeToGreenerStillAffiliate.account,
        )
      }
    },
  })
}

export const useUpgradeToReferringCustomer = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: upgradeToReferringCustomer,
    onSuccess: data => {
      if (data.upgradeToReferringCustomer.account) {
        queryClient.setQueryData<AccountFragment>(
          queryKeyStore.account.detail.queryKey,
          data.upgradeToReferringCustomer.account,
        )
      }
    },
  })
}

export const useUpdateAccount = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateAccount,
    onSuccess: (data, variables) => {
      if (data.updateAccount.account) {
        queryClient.setQueryData<AccountFragment>(
          queryKeyStore.account.detail.queryKey,
          data.updateAccount.account,
        )
        if (variables.account.plan || variables.subscription) {
          queryClient.invalidateQueries({
            queryKey: queryKeyStore.subscription._def,
          })
        }
      }
    },
  })
}

export const useUpdateStorefrontMugshot = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: fetching.updateStorefrontMugshot,
    onSuccess: data => {
      queryClient.setQueryData<AccountFragment>(
        queryKeyStore.account.detail.queryKey,
        account => {
          return (
            account && {
              ...account,
              storefront: data.updateStorefrontMugshot.storefront,
            }
          )
        },
      )
    },
  })
}

export const useUpdateAccountSettings = () => {
  const queryClient = useQueryClient()
  const queryKey = queryKeyStore.account.detail.queryKey

  return useMutation({
    mutationFn: (variables?: AccountSettingsInput) =>
      updateAccount({ account: { settings: variables } }),
    onMutate: async variables => {
      await queryClient.cancelQueries({ queryKey })
      const accountSnapShot =
        queryClient.getQueryData<AccountFragment>(queryKey)
      if (accountSnapShot) {
        queryClient.setQueryData<AccountFragment>(queryKey, old => {
          if (old && old.settings) {
            const settings = {
              defaultBackPanel: variables?.defaultBackPanelId
                ? { id: variables?.defaultBackPanelId }
                : (old.settings.defaultBackPanel ?? null),
              shouldPrintRecipientCompany:
                variables?.printRecipientCompany ??
                old.settings?.shouldPrintRecipientCompany,
              shouldReceiveNotificationEmails:
                variables?.receivesNotificationEmails ??
                old.settings?.shouldReceiveNotificationEmails,
              shouldReceivePromoEmails:
                variables?.receivesPromoEmails ??
                old.settings?.shouldReceivePromoEmails,
              shouldReceiveReceiptEmails:
                variables?.receivesReceiptEmails ??
                old.settings?.shouldReceiveReceiptEmails,
              shouldReceiveReminderEmails:
                variables?.receivesReminderEmails ??
                old.settings?.shouldReceiveReminderEmails,
              shouldReceiveMarketingSms:
                variables?.receivesMarketingSms ??
                old.settings?.shouldReceiveMarketingSms,
              remindersAdvanceNoticeDays:
                variables?.remindersAdvanceNoticeDays ??
                old.settings?.remindersAdvanceNoticeDays,
              remindersAdvanceNoticeDaysInternational:
                variables?.remindersAdvanceNoticeDaysInternational ??
                old.settings?.remindersAdvanceNoticeDaysInternational,
            }

            return {
              ...old,
              settings: { ...old.settings, ...settings },
            }
          }
          return undefined
        })
      }
      return { accountSnapShot }
    },
    onSettled: (data, _error, _variables, context) => {
      if (data?.updateAccount) {
        queryClient.setQueryData<AccountFragment>(
          queryKey,
          data.updateAccount.account,
        )
      } else {
        if (context?.accountSnapShot) {
          queryClient.setQueryData<AccountFragment>(
            queryKey,
            context?.accountSnapShot,
          )
        }
      }
    },
  })
}

export const useUpdateSourceBillingAddress = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.updateSourceBillingAddress,
    onSuccess: _data => {
      queryClient.invalidateQueries(queryKeyStore.account.detail)
    },
  })
}

export const useUpdateCard = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateCard,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(
        queryKeyStore.card.detail({ id: variables.card.id }),
      )
    },
  })
}

export const useGetOrCreateEditableCardForRecipient = () => {
  return useMutation({
    mutationFn: fetching.getOrCreateEditableCardForRecipient,
  })
}

export const useUpdateRecipient = () => {
  return useMutation({ mutationFn: fetching.updateRecipient })
}

export const useSendPausedRecipients = () => {
  return useMutation({ mutationFn: fetching.sendPausedRecipients })
}

export const useUpdateUserLabelOverrides = () => {
  return useMutation({ mutationFn: updateUserLabelOverrides })
}

export const useUpdateCampaign = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.updateCampaign,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(
        queryKeyStore.campaign.detail({ id: variables.campaign.id }),
      )
    },
  })
}

export const useDeleteCampaign = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.deleteCampaign,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(
        queryKeyStore.campaign.detail({ id: variables.id }),
      )
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.campaign.listPaginated._def,
      })
    },
  })
}

export const useShareCampaign = () => {
  return useMutation({
    mutationFn: fetching.shareCampaign,
  })
}

export const useCreateOrder = () =>
  useMutation({ mutationFn: fetching.createOrder })

export const useDeleteOrder = () =>
  useMutation({ mutationFn: fetching.deleteOrder })

export const useBulkDeleteOrders = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.bulkDeleteOrders,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.order._def,
      })
    },
  })
}

export const useCreateOrderFromDigitalCard = () =>
  useMutation({ mutationFn: fetching.createOrderFromDigitalCard })

export const useCreateCampaign = () =>
  useMutation({ mutationFn: fetching.createCampaign })

export const useUpdateOrder = () =>
  useMutation({ mutationFn: fetching.updateOrder })

export const useSendOrder = () =>
  useMutation({ mutationFn: fetching.sendOrder })

export const useUpdateProductionInfo = () =>
  useMutation({ mutationFn: fetching.updateProductionInfo })

export const useUpdateReminder = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: fetching.updateReminder,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.alerts.infinite._def,
      })
    },
  })
  return mutation
}

export const useRevertBulkOrder = () => {
  const account = useAccount()
  const actions = useActions()
  const updateOrderMutation = useUpdateOrder()

  return useMutation({
    mutationFn: revertBulkOrder,
    onSuccess: async (data, variables) => {
      actions.savedOrder(data.revertBulkOrder.order)

      if (account.shippingAddress) {
        const returnAddress: AddressInput = {
          firstName: account.shippingAddress.firstName,
          lastName: account.shippingAddress.lastName,
          company: account.shippingAddress.company,
          address1: account.shippingAddress.address1,
          address2: account.shippingAddress.address2,
          city: account.shippingAddress.city,
          state: account.shippingAddress.state,
          postalCode: account.shippingAddress.postalCode,
          country: account.shippingAddress.country,
        }
        const { updateOrder } = await updateOrderMutation.mutateAsync({
          order: { id: variables.id, returnAddress: returnAddress },
        })

        if (updateOrder.order) {
          actions.savedOrder(updateOrder.order)
        }
      }
    },
  })
}

export const useCreateOrUpdateBackPanel = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createOrUpdateBackPanel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.layout._def })
    },
  })
}

export const useDeletePanelLayout = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.deletePanelLayout,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.layout._def })
    },
  })
}

export const useUploadImage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: fetching.uploadImage,
    onSuccess: () => {
      // TODO: update cache directly
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.image._def,
      })
    },
  })
}

export const useCreateImageWithUrl = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: fetching.createImageWithUrl,
    onSuccess: () => {
      // TODO: update cache directly
      queryClient.invalidateQueries({
        queryKey: queryKeyStore.image._def,
      })
    },
  })
}

export const useDeleteImage = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.deleteImage,
    onSuccess: () => {
      // TODO: update cache directly
      queryClient.invalidateQueries({ queryKey: queryKeyStore.image._def })
    },
  })
}

export const useAddFreeCampaign = () =>
  useMutation({ mutationFn: addFreeCampaign })

export const useChangeSubscription = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: changeSubscription,
    onSuccess: data => {
      if (data.changeSubscription.account) {
        queryClient.setQueryData<AccountFragment>(
          queryKeyStore.account.detail.queryKey,
          data.changeSubscription.account,
        )
        queryClient.invalidateQueries({
          queryKey: queryKeyStore.subscription._def,
        })
      }
    },
  })
}

export const useCreateContact = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.createContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.contact._def })
    },
  })
}

export const useCreateContacts = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.createContacts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.contact._def })
    },
  })
}

export const useCreateContactWithInvite = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.createContactWithInvite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.contact._def })
    },
  })
}

export const useCreateContactsWithInvites = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.createContactsWithInvites,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.contact._def })
    },
  })
}

export const useCreateContactWithSpouseChildren = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.createContactWithSpouseChildren,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.contact._def })
    },
  })
}

export const useUpdateContact = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.updateContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.contact._def })
    },
  })
}

export const useDeleteContact = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.deleteContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.contact._def })
    },
  })
}

export const useBulkDeleteContacts = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.bulkDeleteContacts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.contact._def })
    },
  })
}

export const useSeparateContact = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.separateContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.contact._def })
    },
  })
}

export const useShareContact = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.shareContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.contact._def })
    },
  })
}

export const useCreateContactRequest = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.createContactRequest,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeyStore.contact_requests.list)
    },
  })
}

export const useDeleteContactRequest = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.deleteContactRequest,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeyStore.contact_requests.list)
    },
  })
}

export const useUpdateRequestedContact = () =>
  useMutation({
    mutationFn: fetching.updateRequestedContact,
  })

export const useCreateGroup = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.createGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.contact._def })
    },
  })
}

export const useUpdateGroup = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.updateGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.contact._def })
    },
  })
}

export const useDeleteGroup = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.deleteGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.contact._def })
    },
  })
}

export const useUpdateInvite = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.updateInvite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyStore.contact._def })
    },
  })
}

export const useClaimReward = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetching.claimReward,
    onSuccess: data => {
      if (data.claimReward.account) {
        queryClient.setQueryData<AccountFragment>(
          queryKeyStore.account.detail.queryKey,
          data.claimReward.account,
        )
      }
    },
  })
}

export const useCreatePurchase = () => {
  return useMutation({
    mutationFn: fetching.createPurchase,
  })
}

export const useCommitPurchase = () => {
  return useMutation({
    mutationFn: fetching.commitPurchase,
  })
}

export const useUpdateCoachPublicInfoDisplay = () => {
  return useMutation({
    mutationFn: fetching.updateCoachPublicInfoDisplay,
  })
}

export const useShouldDisplayUpsaleTrigger = () => {
  return useMutation({
    mutationFn: fetching.shouldDisplayUpsaleTrigger,
  })
}

export const useUnlockAccountSendCode = () => {
  return useMutation({
    mutationFn: fetching.unlockAccountSendCode,
  })
}

export const useUnlockAccountVerifyCode = () => {
  return useMutation({
    mutationFn: fetching.unlockAccountVerifyCode,
  })
}
